import { DestroyRef, inject, Injectable, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { from, Observable, Subject } from "rxjs";
import { concatMap, map, mergeMap } from "rxjs/operators";
import { ElectronService } from "../../electron.service";
import { FileList, ImageUploadOfflineService } from "../image-upload-offline.service";
import { ImageUploadService } from "../image-upload.service";
import { InspectionImageService } from "../http-api/inspection-image.service";
import { UploadOption } from "../../_objects/inspection-image";
import { IPC_CHANNEL } from "../../_offline/electron.constants";
import { NetworkStatusService } from "../network-status.service";
import { IMAGE_FILETYPE } from "../../app.constants";
import { ImageCreationMapper } from "./image-creation.mapper";
import { FileManagerService } from "../../common-dialog/file-manager/file-manager.service";
import { folderPathOfFile, getFilePathInfo, splitFolderPath } from "../../_utils/file-system-utils";

interface FileData {
  fileName: string;
  filePath: string;
  inspectionId: string;
}

interface FileWatcherArgs {
  folderPath: string;
  inspectionId: string;
}

@Injectable({
  providedIn: "root"
})
export class ExternalSyncUploadService {
  private readonly destroyRef = inject(DestroyRef);

  private readonly isActive = signal(false);
  private readonly queuedFilePaths = signal(new Set<string>());
  private readonly inspectionId = signal<string>("");
  // The watched inspection folder; file paths are made relative to it so a file saved at any depth resolves to its location
  private readonly watchedFolderPath = signal<string>("");

  public readonly uploadCompleted$ = new Subject<void>();
  private queue$ = new Subject<FileList>();

  private static readonly MAX_FILE_RETRIES = 5;
  private static readonly FILE_RETRY_DELAY_MS = 200;

  constructor(
    private electronService: ElectronService,
    private inspectionImageService: InspectionImageService,
    private imageUploadOfflineService: ImageUploadOfflineService,
    private imageUploadService: ImageUploadService,
    private networkStatusService: NetworkStatusService
  ) {}

  public synchronizeFiles(inspectionId: string, folderPath: string): void {
    if (!this.electronService.isElectron()) {
      return;
    }

    // Clean up any existing synchronization first
    this.stopCurrentProccessingFiles();

    this.isActive.set(true);
    this.inspectionId.set(inspectionId);
    this.watchedFolderPath.set(folderPath ?? "");
    this.queuedFilePaths.set(new Set<string>());

    this.queue$ = new Subject<FileList>();

    this.startFileWatcher(inspectionId, folderPath);
    this.initQueueProcessor();
    this.registerFileChangeListener();
  }

  public stopCurrentProccessingFiles(): void {
    if (!this.electronService.isElectron()) {
      return;
    }

    this.isActive.set(false);
    this.clearQueue();
    this.unregisterFileChangeListener();
    this.stopFileWatcher();
  }

  private initQueueProcessor(): void {
    this.queue$
      .pipe(
        concatMap((fileListItem) => {
          if (!this.isActive()) return [];

          return this.uploadImage(fileListItem);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (fileListItem) => {
          this.handleUploadSuccess(fileListItem);
        },
        error: (err) => console.error("Error in queue:", err),
        complete: () => console.log("Queue processor completed")
      });
  }

  private registerFileChangeListener(): void {
    this.unregisterFileChangeListener();

    const handler = async (event: any, fileData: FileData | FileData[]) => {
      const data = Array.isArray(fileData) ? fileData[0] : fileData;

      if (!data) {
        console.error("No file data received");
        return;
      }

      await this.onFileChange(data);
    };

    this.electronService.onEvent("file-changed", handler);
  }

  private unregisterFileChangeListener(): void {
    this.electronService.removeAllListeners("file-changed");
  }

  private enqueueFile(fileListItem: FileList): void {
    if (!this.isActive()) {
      return;
    }

    const queuedPaths = this.queuedFilePaths();
    if (queuedPaths.has(fileListItem.fullPath)) {
      return;
    }

    const updatedPaths = new Set(queuedPaths);
    updatedPaths.add(fileListItem.fullPath);
    this.queuedFilePaths.set(updatedPaths);

    this.queue$.next(fileListItem);
  }

  private handleUploadSuccess(fileListItem: FileList): void {
    this.uploadCompleted$.next();

    const queuedPaths = this.queuedFilePaths();
    const updatedPaths = new Set(queuedPaths);
    updatedPaths.delete(fileListItem.fullPath);
    this.queuedFilePaths.set(updatedPaths);
  }

  private clearQueue(): void {
    if (this.queue$) {
      this.queue$.complete();
    }

    this.queuedFilePaths.set(new Set<string>());
  }

  private async onFileChange(fileData: FileData): Promise<void> {
    if (!this.isActive()) {
      return;
    }

    if (this.shouldSkipFile(fileData.fileName)) {
      return;
    }

    this.inspectionId.set(fileData.inspectionId);

    await this.waitForFileAvailable(fileData.filePath);
    const fileFromDisk = await this.fetchFileFromPath(fileData.filePath);
    const fileList = this.toFileListItem(fileFromDisk, fileData.filePath, fileData.fileName);

    this.enqueueFile(fileList);
  }

  private shouldSkipFile(fileName: string): boolean {
    const fileExtension = getFilePathInfo(fileName)?.fileExtension.toLocaleLowerCase();
    return fileExtension === IMAGE_FILETYPE.EXTENSION_JSON || fileExtension === IMAGE_FILETYPE.EXTENSION_JSN;
  }

  private toFileListItem(file: File, filePath: string, fileName: string): FileList {
    const directory = this.getDirectoryName(filePath);
    const fileListItem = new FileList(file, `${directory}/${fileName}`, filePath, "", "");
    fileListItem.folderPath = this.getFolderPathInsideInspection(filePath);
    return fileListItem;
  }

  /**
   * Folders between the watched inspection folder and the file ("/" separated), so a file saved anywhere in a
   * tree structure resolves to its location. Falls back to the parent folder alone for a file outside the watched folder.
   */
  private getFolderPathInsideInspection(filePath: string): string {
    const root = splitFolderPath(this.watchedFolderPath()).join("/");
    const folders = folderPathOfFile(filePath);
    const isInsideRoot = root && `${folders}/`.toLowerCase().startsWith(`${root}/`.toLowerCase());
    return isInsideRoot ? folders.slice(root.length + 1) : this.getDirectoryName(filePath);
  }

  private async waitForFileAvailable(
    filePath: string,
    maxRetries = ExternalSyncUploadService.MAX_FILE_RETRIES,
    delayMs = ExternalSyncUploadService.FILE_RETRY_DELAY_MS
  ): Promise<void> {
    for (let i = 0; i < maxRetries; i++) {
      try {
        await this.electronService.invoke(IPC_CHANNEL.FILE.CHECK_FILE_ACCESS, { filePath });
        return;
      } catch (error) {
        if (i === maxRetries - 1) {
          throw new Error(`File not available after ${maxRetries} attempts: ${filePath}`);
        }
        await this.delay(delayMs);
      }
    }
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  private startFileWatcher(inspectionId: string, folderPath: string): void {
    const watcherArgs: FileWatcherArgs = { folderPath, inspectionId };

    this.electronService
      .sendCommand(IPC_CHANNEL.FILE_WATCHER.MONITOR, watcherArgs)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => console.log("File watcher start command sent"),
        error: (err) => console.error("Error sending file watcher start command:", err)
      });
  }

  private stopFileWatcher(): void {
    this.electronService.sendCommand(IPC_CHANNEL.FILE_WATCHER.UNMONITOR).subscribe({
      next: () => console.log("File watcher stop command sent"),
      error: (err) => console.error("Error sending file watcher stop command:", err)
    });
  }

  private async fetchFileFromPath(filePath: string): Promise<File> {
    const filesData = await this.electronService.invoke(IPC_CHANNEL.FILE.READ_FILES, { paths: [filePath] });

    const fileFromDisk = filesData[0];
    const blob = new Blob([fileFromDisk.data], { type: fileFromDisk.mimeType });

    return new File([blob], fileFromDisk.fileName, { type: fileFromDisk.mimeType });
  }

  private getDirectoryName(filePath: string): string {
    const normalizedPath = filePath.replace(/\\/g, "/");
    const pathParts = normalizedPath.split("/");
    pathParts.pop(); // Remove filename
    return pathParts.pop() || "";
  }

  private uploadImage(fileListItem: FileList): Observable<FileList> {
    const uploadMethod = this.networkStatusService.isOnline ? this.uploadImageOnline(fileListItem) : this.uploadImageOffline(fileListItem);

    return uploadMethod.pipe(map(() => fileListItem));
  }

  private uploadImageOnline(fileListItem: FileList): Observable<any> {
    const normalizedFileListItem = this.normalizeFileType(fileListItem);
    const files = [normalizedFileListItem];

    return from(this.retrieveJsonFile(normalizedFileListItem)).pipe(
      mergeMap((additionFile) => this.prepareImagesForCreation(files, additionFile)),
      mergeMap((imagesToCreate) => this.createAndUploadImages(imagesToCreate))
    );
  }

  private uploadImageOffline(fileListItem: FileList): Observable<any> {
    this.imageUploadOfflineService.uploadInit(this.inspectionId());

    const normalizedFileListItem = this.normalizeFileType(fileListItem);

    return from(this.retrieveJsonFile(normalizedFileListItem)).pipe(
      mergeMap((additionFile) => this.processOfflineUpload(normalizedFileListItem, additionFile))
    );
  }

  private normalizeFileType(fileListItem: FileList): FileList {
    if (fileListItem.file.type === IMAGE_FILETYPE.MP4_APPLICATION) {
      fileListItem.file = new File([fileListItem.file], fileListItem.file.name, {
        type: IMAGE_FILETYPE.MP4,
        lastModified: fileListItem.file.lastModified
      });
    }
    return fileListItem;
  }

  private async prepareImagesForCreation(files: FileList[], additionFile: FileList | null): Promise<any> {
    if (additionFile) files.push(additionFile);

    return await ImageCreationMapper.toImagesCreationFromFileLists(files, this.inspectionId());
  }

  private createAndUploadImages(imagesToCreate: any): Observable<any> {
    return this.inspectionImageService
      .createInspectionImages(this.inspectionId(), imagesToCreate)
      .pipe(
        mergeMap((createInspectionImagesResponse) =>
          this.imageUploadService.uploadImageToBlobUrl(imagesToCreate, createInspectionImagesResponse.createdImages)
        )
      );
  }

  private processOfflineUpload(fileListItem: FileList, additionFile: FileList | null): Observable<any> {
    const allFiles = additionFile ? [fileListItem, additionFile] : [fileListItem];
    const fileNameList = allFiles.map((f) => f.fileName);

    this.imageUploadOfflineService.uploadFileList(allFiles, fileNameList, null, UploadOption.Skip, true);

    return this.imageUploadOfflineService.uploadSuccessObservable;
  }

  private async retrieveJsonFile(fileListItem: FileList): Promise<FileList | null> {
    const fileExtension = getFilePathInfo(fileListItem.fullPath)?.fileExtension;

    if (fileExtension !== IMAGE_FILETYPE.EXTENSION_MP4) {
      return null;
    }

    const baseFileName = getFilePathInfo(fileListItem.fullPath)?.fileName;
    const jsonExtensions = [IMAGE_FILETYPE.EXTENSION_JSON, IMAGE_FILETYPE.EXTENSION_JSON];

    // Try both .json and .jsn extensions
    for (const ext of jsonExtensions) {
      const partnerFilePath = fileListItem.fullPath.replace(/\.mp4$/i, `.${ext}`);
      const partnerFileName = `${baseFileName}.${ext}`;

      try {
        const fileWithDetectedChange = await this.fetchFileFromPath(partnerFilePath);

        return this.toFileListItem(fileWithDetectedChange, partnerFilePath, partnerFileName);
      } catch (error) {
        // Continue to next extension if this one fails
        continue;
      }
    }

    console.error("No partner file to retrieve");
    return null;
  }
}
